import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { botGet, botStatusApi, msSinceLocalWrite, onQueued, openLiveStream } from "@/lib/api";

const DataContext = createContext(null);

// Testo leggibile per le modifiche arrivate da Discord.
function describe(ev) {
  const d = ev.data || {};
  const name = d.name || d.displayName || d.username || "";
  const map = {
    "role.create": `Ruolo «${name}» creato`, "role.update": `Ruolo «${name}» modificato`, "role.delete": `Ruolo «${name}» eliminato`,
    "channel.create": `Canale «${name}» creato`, "channel.update": `Canale «${name}» modificato`, "channel.delete": `Canale «${name}» eliminato`,
    "guild.update": "Impostazioni del server modificate",
  };
  return map[ev.event];
}

const STRUCTURE_EVENTS = /^(role|channel|guild)\./;

export function DataProvider({ children }) {
  const [roles, setRoles] = useState([]);
  const [channels, setChannels] = useState([]);
  const [meta, setMeta] = useState(null);
  const [guild, setGuild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [botStatus, setBotStatus] = useState(null);
  const [live, setLive] = useState(false);
  // Aumenta a ogni modifica di ruoli/canali arrivata da Discord (non da questo browser).
  const [remoteVersion, setRemoteVersion] = useState(0);
  const refreshTimer = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c, m, g] = await Promise.all([
        botGet("roles"), botGet("channels"), botGet("meta"), botGet("guild"),
      ]);
      setRoles(Array.isArray(r) ? r : []);
      setChannels(Array.isArray(c) ? c : []);
      setMeta(m || null);
      setGuild(g || null);
    } catch (e) {
      // bot non raggiungibile: si tengono i dati precedenti
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    try { const s = await botStatusApi.get(); setBotStatus(s); return s; } catch { return null; }
  }, []);

  const scheduleRefresh = useCallback(() => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(refresh, 600);
  }, [refresh]);

  useEffect(() => { refresh(); refreshStatus(); }, [refresh, refreshStatus]);

  // Stato del bot: aggiornato ogni 30 secondi e subito dopo gli eventi rilevanti.
  useEffect(() => {
    const t = setInterval(refreshStatus, 30000);
    return () => clearInterval(t);
  }, [refreshStatus]);

  useEffect(() => onQueued((d) => {
    toast.warning("Bot non raggiungibile", { description: `${d.message} (${d.pending} in coda)`, duration: 7000 });
    refreshStatus();
  }), [refreshStatus]);

  // Canale in tempo reale: Discord → sito.
  useEffect(() => openLiveStream((ev) => {
    const e = ev.event || "";
    if (STRUCTURE_EVENTS.test(e)) {
      scheduleRefresh();
      if (msSinceLocalWrite() > 8000) {
        setRemoteVersion((v) => v + 1);
        const text = describe(ev);
        if (text) toast("Modifica da Discord", { description: text, icon: "🔄" });
      }
    }
    if (e.startsWith("member.")) window.dispatchEvent(new CustomEvent("kalyx:members", { detail: ev }));
    if (e === "site.refresh") scheduleRefresh();
    if (e.startsWith("site.queue") || e.startsWith("site.status") || e.startsWith("bot.")) refreshStatus();
    if (e === "site.queue" && ev.data?.applied) toast.success(`${ev.data.applied} modifiche in coda applicate su Discord`);
    window.dispatchEvent(new CustomEvent("kalyx:live", { detail: ev }));
  }, setLive), [scheduleRefresh, refreshStatus]);

  const roleName = (id) => roles.find((r) => r.id === id)?.name || id;
  const channelName = (id) => channels.find((c) => c.id === id)?.name || id;

  return (
    <DataContext.Provider value={{
      roles, channels, meta, guild, loading, refresh, roleName, channelName, setGuild,
      botStatus, refreshStatus, setBotStatus, live, remoteVersion,
    }}>
      {children}
    </DataContext.Provider>
  );
}

export const useData = () => useContext(DataContext);
